import type { StripeConnectInstance } from '@stripe/connect-js'

type OrganizationIdInput = string | number | null | undefined
// The registry tolerates an absent or synchronous logout; the SDK completes asynchronously.
interface ConnectSessionInstance {
  logout?: () => ReturnType<StripeConnectInstance['logout']> | void
}
interface ConnectSession {
  organizationId: string | null
  connectInstance: ConnectSessionInstance
}

const activeStripeConnectSessions = new Map<string, ConnectSession>()
let activeStripeConnectLogoutPromise: Promise<void> | null = null

export const STRIPE_CONNECT_LOGOUT_WAIT_MS = 500

function normalizeOrganizationId(organizationId: OrganizationIdInput): string | null {
  return organizationId === null || organizationId === undefined ? null : String(organizationId)
}

export function registerActiveStripeConnectInstance({
  organizationId,
  connectInstance,
  accountContext = 'current',
}: {
  organizationId?: OrganizationIdInput
  connectInstance?: ConnectSessionInstance | null
  accountContext?: string
}): (() => void) | undefined {
  if (!connectInstance) {
    return
  }

  const session = {
    organizationId: normalizeOrganizationId(organizationId),
    connectInstance,
  }
  activeStripeConnectSessions.set(accountContext, session)
  return () => {
    if (activeStripeConnectSessions.get(accountContext) === session)
      activeStripeConnectSessions.delete(accountContext)
  }
}

export function logoutActiveStripeConnectInstance(): Promise<void> {
  const sessions = [...activeStripeConnectSessions.values()]

  if (!sessions.length) {
    return activeStripeConnectLogoutPromise ?? Promise.resolve()
  }

  // Clear first so overlapping logout/session-expiration handlers stay idempotent.
  activeStripeConnectSessions.clear()

  let resolveLogoutCompletion: () => void = () => {}
  const logoutCompletion = new Promise<void>((resolve) => {
    resolveLogoutCompletion = resolve
  })
  activeStripeConnectLogoutPromise = logoutCompletion
  void logoutCompletion.finally(() => {
    if (activeStripeConnectLogoutPromise === logoutCompletion) {
      activeStripeConnectLogoutPromise = null
    }
  })

  let stripeLogoutPromise: Promise<void[]>

  try {
    stripeLogoutPromise = Promise.all(
      sessions.map(async ({ connectInstance }) => {
        try {
          await connectInstance.logout?.()
        } catch {
          /* Best-effort across every mounted account. */
        }
      })
    )
  } catch {
    // Stripe logout is best-effort and must never block OneStem navigation or logout.
    resolveLogoutCompletion()
    return logoutCompletion
  }

  // Stripe resolves logout after its session is invalidated. Give explicit auth boundaries a short
  // completion window while guaranteeing that a stalled SDK call cannot hold up OneStem.
  const timeoutId = setTimeout(resolveLogoutCompletion, STRIPE_CONNECT_LOGOUT_WAIT_MS)

  void stripeLogoutPromise.finally(() => {
    clearTimeout(timeoutId)
    resolveLogoutCompletion()
  })

  return logoutCompletion
}

export function logoutStripeConnectForOrganizationChange(
  nextOrganizationId: OrganizationIdInput
): Promise<void> | null {
  const normalizedNextOrganizationId = normalizeOrganizationId(nextOrganizationId)

  if (
    normalizedNextOrganizationId === null ||
    ![...activeStripeConnectSessions.values()].some(
      (session) => session.organizationId !== normalizedNextOrganizationId
    )
  ) {
    return activeStripeConnectLogoutPromise
  }

  return logoutActiveStripeConnectInstance()
}

export function shouldWaitForStripeConnectOrganizationChange(
  nextOrganizationId: OrganizationIdInput
): boolean {
  const normalizedNextOrganizationId = normalizeOrganizationId(nextOrganizationId)

  return Boolean(
    activeStripeConnectLogoutPromise ||
    (normalizedNextOrganizationId !== null &&
      [...activeStripeConnectSessions.values()].some(
        (session) => session.organizationId !== normalizedNextOrganizationId
      ))
  )
}
